import { type Catalogue, messages } from '@rythmo/messages';
import type { Locale } from '@rythmo/shared';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { pickLocale } from './locale.js';
import { WORDS, type Words } from './words.js';

const STORAGE_KEY = 'rythmo.admin.locale';

interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** The console's own text. */
  t: Words;
  /** The shared words: roles, the domain, refusals. */
  m: Catalogue;
}

const I18nContext = createContext<I18n | null>(null);

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    pickLocale(readStored(), navigator.languages ?? [navigator.language]),
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18n>(
    () => ({
      locale,
      setLocale: (next) => {
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // A private window may refuse storage; the choice still holds for this visit.
        }
        setLocaleState(next);
      },
      t: WORDS[locale],
      m: messages(locale),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (value === null) throw new Error('useI18n outside I18nProvider');
  return value;
}
